import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const ROLES = [
  "Senior Full-Stack Engineer",
  "AI Systems Engineer",
  "Cloud Architect",
  "Technical PM",
  "Delivery Lead",
  "Cybersecurity Lead",
  "Cohort — Student / New Grad",
];

export default function CareersApply() {
  const [params] = useSearchParams();
  const preRole = params.get("role") || "";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", role: preRole || ROLES[0],
    location: "", experience_years: "",
    portfolio_url: "", linkedin_url: "", cover_letter: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.cover_letter) {
      toast.error("ERROR · name, email, and cover letter are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.experience_years === "") delete payload.experience_years;
      else payload.experience_years = Number(payload.experience_years);
      ["phone", "location", "portfolio_url", "linkedin_url"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await api.post("/applications", payload);
      setDone(true);
    } catch (err) {
      toast.error(`ERROR · ${formatApiErrorDetail(err?.response?.data?.detail)}`);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-24" data-testid="apply-success">
        <div className="te-terminal-window">
          <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
              <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
              <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
              traceelements --application-received
            </div>
            <div className="font-mono text-[11px] text-[#FF3355]">RECEIVED</div>
          </div>
          <div className="p-10 lg:p-14 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 border-2 border-[#FF3355] mb-8">
              <Check size={28} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Application received.</h2>
            <p className="mt-4 text-[#C5C6C7] max-w-lg mx-auto">
              Your submission is in the founders&rsquo; queue. We respond to every application within 10 business days.
            </p>
            <Link to="/careers" data-testid="apply-back-careers" className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355] hover:text-[#F5F5F7]">
              <ArrowLeft size={14} /> back to careers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0B0C10]" data-testid="careers-apply-page">
      <section className="py-16 lg:py-20">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <Link to="/careers" className="font-mono text-xs uppercase tracking-[0.22em] text-[#C5C6C7] hover:text-[#FF3355] inline-flex items-center gap-2">
            <ArrowLeft size={14} /> back to careers
          </Link>
          <div className="mt-10 mb-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-4">// CAREERS.APPLY</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
              Apply to <span className="text-[#FF3355]">TraceElements.</span>
            </h1>
            <p className="mt-6 text-[#C5C6C7] max-w-2xl">
              10 business days end-to-end. Paid take-home. Founder-led final.
            </p>
          </div>

          <form onSubmit={submit} className="te-terminal-window" data-testid="apply-form">
            <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
              </div>
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">~/traceelements/apply.sh</div>
              <div className="font-mono text-[11px] text-[#FF3355]">READY</div>
            </div>

            <div className="p-6 lg:p-10 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <F label="name" value={form.name} onChange={(v) => set("name", v)} testid="apply-name" required />
                <F label="direct email" type="email" value={form.email} onChange={(v) => set("email", v)} testid="apply-email" required />
                <F label="phone (optional)" value={form.phone} onChange={(v) => set("phone", v)} testid="apply-phone" />
                <F label="location" value={form.location} onChange={(v) => set("location", v)} testid="apply-location" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">&gt; target role</label>
                  <select
                    value={form.role}
                    onChange={(e) => set("role", e.target.value)}
                    data-testid="apply-role"
                    className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7]"
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <F label="years of experience" type="number" value={form.experience_years} onChange={(v) => set("experience_years", v)} testid="apply-experience" min={0} max={60} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <F label="portfolio / github URL" value={form.portfolio_url} onChange={(v) => set("portfolio_url", v)} testid="apply-portfolio" />
                <F label="linkedin URL" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} testid="apply-linkedin" />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">&gt; cover letter</label>
                <textarea
                  value={form.cover_letter}
                  onChange={(e) => set("cover_letter", e.target.value)}
                  rows={7}
                  placeholder="// tell us the most difficult system you've shipped, and what you learned."
                  data-testid="apply-cover-letter"
                  className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none p-4 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40 resize-y"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-[#1F2833]">
                <div className="font-mono text-xs text-[#C5C6C7]"><span className="text-[#FF3355]">&gt;</span> we respond within 10 business days.</div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="apply-submit"
                  className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] disabled:opacity-50"
                >
                  {busy ? <><Loader2 size={14} className="animate-spin" /> SUBMITTING</> : "[ SUBMIT APPLICATION ]"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, required, min, max }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2 block">&gt; {label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required} min={min} max={max}
        className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40"
      />
    </div>
  );
}
